import React, { useMemo, useCallback, useState, useEffect, useRef } from 'react';
import { TableProvider } from './context/TableContext';
import TaskTable from './TaskTable';
import DeleteTaskModal from './components/DeleteTaskModal';
import CampaignHeader from './components/CampaignHeader';
import FokusOverlay from '../shared/FokusOverlay';
import { getStoredColumnSizing, saveColumnSizing } from './utils/columnSizingStorage';
import { calculateLockStatesWithParentContext } from '../FlowchartView/utils/dependencyUtils';
import { useWaitGateNow } from '../shared/ConditionsDropdown/utils/useWaitGateNow';
import { filterActionableTasks } from '../shared/utils/taskAvailabilityUtils';
import { useCampaignProgressStats } from '../shared/CampaignHeader/hooks/useCampaignProgressStats';
import { sortTasks } from '../components/SortDropdown';
import { applyFilters } from '../shared/filterUtils';
import { groupTasksIntoSwimlanes } from '../config/swimlaneConfig';
import { useTableClipboard } from './hooks/useTableClipboard';
import './styles/index.css';

/**
 * TableView Component (TanStack Table version)
 * 
 * Displays tasks organized by category in a table format.
 * Uses TanStack Table for:
 * - Column resizing
 * - Row selection
 * - Sticky columns
 * - Row expansion (subtasks)
 */
const TableViewNew = ({
  tasks = [],
  categories = [],
  allTags = [],
  statuses = [],
  nestedSprints = [],
  campaignId = '',
  campaignName = '',
  campaignIcon = null,
  campaignProgress = 0,
  breadcrumbPath = [],
  onNavigateToParent,
  onSelectSprint,
  onSprintIconChange,
  onStatusChange,
  onPriorityChange,
  onDifficultyChange,
  onDependencyChange,
  onConditionsChange,
  onDeleteTask,
  onDeleteTasks,
  onPasteTasks,
  onDuplicateTask,
  onAddTask,
  onAddDecision,
  onAddWait,
  onRenameTask,
  onCategoryChange,
  onRenameCategory,
  onDeleteCategory,
  // Tag actions (multi-select)
  onAddTag,
  onRemoveTag,
  onSetPrimaryTag,
  onRenameTag,
  onDeleteTag,
  onAddSubtask,
  onFocusTask,
  onOpenNestedSprint,
  onRemoveNestedSprint,
  onSelectionChange,
  onAddStatus,
  onDeleteStatus,
  onRenameStatus,
  onSetStatusAutoSync,
  onRenameSprint,
  onIconChange,
  onArchiveTask,
  onUnarchiveTask,
  onDecisionAnswerChange,
  onDecisionOptionsChange,
  showArchived = false,
  showActionableOnly = false,
  // Sort setting
  sortSetting = 'manual',
  // Filter settings
  filters = {},
  // Swimlane setting (same as Kanban - 'none', 'tag', 'priority', etc.)
  // Default is 'none' (no grouping) - previously table always grouped by primary tag
  swimlaneSetting = 'none',
  swimlaneContext = {},
  // FOKUS mode props
  fokusMode = null, // { isFokusMode, fokusFilteredTasks, isPrimaryFokused, isDependencyTask }
  // Column visibility
  columnVisibility = {},
  // Custom fields
  customFields = [],
  onCustomFieldValueChange,
  // Details sidebar
  onOpenDetails,
  onOpenAttachments,
  // View navigation (for "Open in view" dropdown)
  currentView = 'table',
  customViews = [],
  activeCustomViewId = null,
  onViewChange,
  onSelectCustomView,
  onGoToTaskInView,
  // Empty state arrow indicator
  onShowAddMissionArrow,
  // Deliverables warning modal
  onSubmitDeliverable,
  localSprint = null,
  allSprints = [],
}) => {
  // Delete confirmation modal state
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);
  
  // Track row selection per table (keyed by category/group id)
  // This allows us to aggregate selections across all tables
  const [selectionsPerTable, setSelectionsPerTable] = useState({});
  
  // Aggregate all selections into a single Set
  const selectedRowIds = useMemo(() => {
    const allIds = new Set();
    Object.values(selectionsPerTable).forEach(ids => {
      ids.forEach(id => allIds.add(id));
    });
    return allIds;
  }, [selectionsPerTable]);
  
  // Store onSelectionChange in a ref to avoid infinite loops
  const onSelectionChangeRef = useRef(onSelectionChange);
  onSelectionChangeRef.current = onSelectionChange;
  
  // Track if we're in FOKUS mode to prevent selection event dispatch during FOKUS
  const isFokusModeRef = useRef(fokusMode?.isFokusMode);
  isFokusModeRef.current = fokusMode?.isFokusMode;
  
  // Dispatch selection change event for FOKUS mode integration
  // Only dispatch when NOT in FOKUS mode to prevent infinite loops
  useEffect(() => {
    // Don't dispatch selection changes while in FOKUS mode
    // (FOKUS mode filters tasks, which can trigger spurious selection changes)
    if (isFokusModeRef.current) return;
    
    window.dispatchEvent(new CustomEvent('tableSelectionChange', {
      detail: { selectedTaskIds: selectedRowIds, view: 'table' }
    }));
    // Also call the prop callback with aggregated selection
    onSelectionChangeRef.current?.(Array.from(selectedRowIds));
  }, [selectedRowIds]);

  // Scroll to task helper (for paste navigation and focusMission)
  const scrollToTask = useCallback((taskId) => {
    const rowElement = document.querySelector(`[data-row-id="${taskId}"]`);
    if (rowElement) {
      // Use instant scroll so element is immediately visible
      rowElement.scrollIntoView({ behavior: 'instant', block: 'center' });
      
      // Flash highlight immediately after instant scroll
      // Duration matches the CSS animation (1.8s pulse + fade)
      rowElement.classList.add('table-view__row--highlighted');
      const stickyCells = rowElement.querySelectorAll('.table-view__cell--sticky, .table-view__cell--checkbox, .table-view__cell--select, .table-view__cell--name');
      stickyCells.forEach(cell => cell.classList.add('table-view__cell--highlighted'));
      
      setTimeout(() => {
        rowElement.classList.remove('table-view__row--highlighted');
        stickyCells.forEach(cell => cell.classList.remove('table-view__cell--highlighted'));
      }, 2000);
    }
  }, []);

  // Setter for selection (clears all table selections and sets new ones)
  const setSelectedRowIds = useCallback((newSelection) => {
    // Convert Set to array if needed
    const newIds = newSelection instanceof Set ? Array.from(newSelection) : newSelection;
    // Clear all table selections and set the new selection in a special "__clipboard__" table
    setSelectionsPerTable({ __clipboard__: newIds });
  }, []);

  // Clipboard hook for copy/paste/duplicate operations
  useTableClipboard({
    tasks,
    selectedRowIds,
    setSelectedRowIds,
    nestedSprints,
    allSprints,
    allTags: categories, // categories are the tags in this context
    customFields,
    onPasteTasks,
    onDeleteTasks,
    scrollToTask,
  });

  // Listen for focusMission event from search navigation
  useEffect(() => {
    const handleFocusMission = (e) => {
      const { missionId, view } = e.detail;
      console.log('[TableViewNew] focusMission event received:', { missionId, view });
      if (view !== 'table' || !missionId) {
        console.log('[TableViewNew] Ignoring event (wrong view or no missionId)');
        return;
      }
      
      // Use the shared scrollToTask helper
      console.log('[TableViewNew] Scheduling scrollToTask');
      setTimeout(() => {
        console.log('[TableViewNew] Calling scrollToTask:', missionId);
        scrollToTask(missionId);
      }, 100);
    };
    
    window.addEventListener('focusMission', handleFocusMission);
    return () => window.removeEventListener('focusMission', handleFocusMission);
  }, [scrollToTask]);

  // Shared column sizing state - managed at top level, shared to all tables
  const [columnSizing, setColumnSizing] = useState(() => 
    getStoredColumnSizing(campaignId)
  );

  // Re-initialize when campaign changes
  useEffect(() => {
    setColumnSizing(getStoredColumnSizing(campaignId));
  }, [campaignId]);

  // Save to localStorage when column sizing changes
  useEffect(() => {
    if (Object.keys(columnSizing).length > 0) {
      saveColumnSizing(campaignId, columnSizing);
    }
  }, [columnSizing, campaignId]);

  // Handle column sizing change from any table - updates all tables
  const handleColumnSizingChange = useCallback((updater) => {
    setColumnSizing(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      return next;
    });
  }, []);

  // Handle selection change from individual tables
  // tableId is the group/category id to track selections per table
  const handleTableSelectionChange = useCallback((tableId, selectedIds) => {
    const idsArray = Array.isArray(selectedIds) ? selectedIds : [];
    setSelectionsPerTable(prev => ({
      ...prev,
      [tableId]: idsArray
    }));
    // Note: onSelectionChange will be called via the useEffect on selectedRowIds
  }, []);

  // Calculate lock states for filtering and display (always calculate for locked indicators)
  const waitNow = useWaitGateNow(tasks);
  const lockStates = useMemo(() => {
    return calculateLockStatesWithParentContext(tasks, nestedSprints, statuses, {
      currentSprint: localSprint,
      allSprints,
      now: waitNow,
    });
  }, [tasks, nestedSprints, statuses, localSprint, allSprints, waitNow]);

  const onNavigateToParentMission = useCallback(
    ({ parentSprintId, parentMissionId }) => {
      const parentSprint = allSprints?.find((s) => s.id === parentSprintId);
      if (parentSprint && onSelectSprint) {
        onSelectSprint(parentSprint, { view: currentView || 'table', missionId: parentMissionId });
      }
    },
    [allSprints, onSelectSprint, currentView],
  );

  // Apply standard filters (including archive status and custom fields)
  const baseFilteredTasks = useMemo(() => {
    return applyFilters(tasks, filters, showArchived, lockStates, nestedSprints, statuses, false, customFields);
  }, [tasks, filters, showArchived, lockStates, nestedSprints, statuses, customFields]);

  // Apply actionable filter if enabled
  const actionableFilteredTasks = useMemo(() => {
    if (!showActionableOnly) return baseFilteredTasks;
    return filterActionableTasks(baseFilteredTasks, lockStates, nestedSprints, statuses);
  }, [baseFilteredTasks, showActionableOnly, lockStates, nestedSprints, statuses]);

  // Apply sorting
  const sortedTasks = useMemo(() => {
    return sortTasks(actionableFilteredTasks, sortSetting, customFields);
  }, [actionableFilteredTasks, sortSetting, customFields]);
  
  // Apply FOKUS mode filtering (if active)
  const filteredTasks = useMemo(() => {
    if (fokusMode?.isFokusMode && fokusMode.fokusFilteredTasks) {
      // In FOKUS mode, only show tasks that are in the fokus set
      const fokusIds = new Set(fokusMode.fokusFilteredTasks.map(t => t.id));
      return sortedTasks.filter(task => fokusIds.has(task.id));
    }
    return sortedTasks;
  }, [sortedTasks, fokusMode]);

  // Group tasks based on swimlane setting
  // When swimlaneSetting is 'none', show a flat list (single group with all tasks)
  // When swimlaneSetting is set, group by that attribute (tag, priority, difficulty, etc.)
  const groupedTasks = useMemo(() => {
    // Use swimlane grouping if enabled
    if (swimlaneSetting && swimlaneSetting !== 'none') {
      const swimlanes = groupTasksIntoSwimlanes(filteredTasks, swimlaneSetting, swimlaneContext);
      if (swimlanes) {
        // Convert swimlane format to the format expected by TaskTable
        return swimlanes.map(swimlane => ({
          id: swimlane.id,
          category: {
            name: swimlane.label,
            color: swimlane.color || '#6b7280',
            isUncategorized: swimlane.id === '__empty__',
          },
          tasks: swimlane.tasks,
        }));
      }
    }
    
    // No grouping (swimlaneSetting is 'none') - return single group with all tasks
    // Uses the exact same header as swimlane groups
    return [{
      id: '__all__',
      category: {
        name: 'All Missions',
        color: '#6b7280', // Neutral gray
        isUncategorized: true, // Prevents editing the title
      },
      tasks: filteredTasks,
    }];
  }, [filteredTasks, swimlaneSetting, swimlaneContext]);

  const {
    completedCount,
    totalCount,
    remainingMinutes,
    remainingMinMinutes,
    missingEstCount,
    remainingBreakdown,
  } = useCampaignProgressStats(tasks, statuses, nestedSprints, allSprints);

  return (
    <TableProvider
      allTasks={tasks}
      allCategories={categories}
      allTags={allTags}
      statuses={statuses}
      nestedSprints={nestedSprints}
      allSprints={allSprints}
      lockStates={lockStates}
      columnSizing={columnSizing}
      onColumnSizingChange={handleColumnSizingChange}
      columnVisibility={columnVisibility}
      onStatusChange={onStatusChange}
      onPriorityChange={onPriorityChange}
      onDifficultyChange={onDifficultyChange}
      onDependencyChange={onDependencyChange}
      onConditionsChange={onConditionsChange}
      onDeleteTask={onDeleteTask}
      onDuplicateTask={onDuplicateTask}
      onAddTask={onAddTask}
      onAddDecision={onAddDecision}
      onAddWait={onAddWait}
      onRenameTask={onRenameTask}
      onCategoryChange={onCategoryChange}
      onRenameCategory={onRenameCategory}
      onDeleteCategory={onDeleteCategory}
      onAddTag={onAddTag}
      onRemoveTag={onRemoveTag}
      onSetPrimaryTag={onSetPrimaryTag}
      onRenameTag={onRenameTag}
      onDeleteTag={onDeleteTag}
      onAddSubtask={onAddSubtask}
      onFocusTask={onFocusTask}
      onOpenDetails={onOpenDetails}
      onOpenAttachments={onOpenAttachments}
      onOpenNestedSprint={onOpenNestedSprint}
      onRemoveNestedSprint={onRemoveNestedSprint}
      onIconChange={onIconChange}
      onArchiveTask={onArchiveTask}
      onUnarchiveTask={onUnarchiveTask}
      onDecisionAnswerChange={onDecisionAnswerChange}
      onDecisionOptionsChange={onDecisionOptionsChange}
      onAddStatus={onAddStatus}
      onDeleteStatus={onDeleteStatus}
      onRenameStatus={onRenameStatus}
      onSetStatusAutoSync={onSetStatusAutoSync}
      onSelectionChange={onSelectionChange}
      onOpenDeleteConfirmation={(taskId, taskName) => setDeleteConfirmation({ taskId, taskName })}
      campaignId={campaignId}
      currentView={currentView}
      customViews={customViews}
      activeCustomViewId={activeCustomViewId}
      customFields={customFields}
      onCustomFieldValueChange={onCustomFieldValueChange}
      onSubmitDeliverable={onSubmitDeliverable}
      onNavigateToParentMission={onNavigateToParentMission}
    >
      <div className={`table-view${fokusMode?.isFokusMode ? ' table-view--fokus-mode' : ''}`}>
        {/* FOKUS mode overlay - dithered blue vignette */}
        {/* skipEnterAnimation=true because if we're rendering, fokus was already active */}
        {fokusMode?.isFokusMode && <FokusOverlay skipEnterAnimation />}
        
        <CampaignHeader
          campaignName={campaignName}
          campaignIcon={campaignIcon}
          campaignProgress={campaignProgress}
          breadcrumbPath={breadcrumbPath}
          completedCount={completedCount}
          totalCount={totalCount}
          remainingMinutes={remainingMinutes}
          remainingMinMinutes={remainingMinMinutes}
          missingEstCount={missingEstCount}
          remainingBreakdown={remainingBreakdown}
          onSelectSprint={onSelectSprint}
          onSprintIconChange={onSprintIconChange}
          onRenameSprint={onRenameSprint}
        />

        <div className="table-view__content">
          {groupedTasks
            .filter(group => group.tasks.length > 0)
            .map((group) => (
              <TaskTable
                key={group.id}
                groupId={group.id}
                tasks={group.tasks}
                category={group.category}
                onSelectionChange={handleTableSelectionChange}
              />
            ))}

          {/* Empty state */}
          {tasks.length === 0 && (
            <div className="table-view__empty">
              <div className="table-view__empty-icon">🎯</div>
              <h3 className="table-view__empty-title">No missions yet</h3>
              <p className="table-view__empty-text">
                Click{' '}
                <button 
                  className="table-view__empty-link" 
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    onShowAddMissionArrow?.();
                  }}
                >
                  Add Mission
                </button>
                {' '}to create your first mission
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteTaskModal
        isOpen={!!deleteConfirmation}
        taskName={deleteConfirmation?.taskName || ''}
        onCancel={() => setDeleteConfirmation(null)}
        onConfirmDelete={() => {
          if (deleteConfirmation) {
            onDeleteTask?.(deleteConfirmation.taskId);
          }
        }}
        onArchiveInstead={() => {
          if (deleteConfirmation) {
            onArchiveTask?.(deleteConfirmation.taskId);
          }
        }}
      />
    </TableProvider>
  );
};

export default TableViewNew;